import React from 'react';
import { dayMonth, percent, signedDelta } from './lib/activityFormat';

/**
 * ▲/▼ N pts against the period before. Nothing when there is no previous
 * figure. A zero change reads "same", as the other tiles do.
 */
const Pts = ({ current, previous }) => {
  if (current == null || previous == null) return null;
  const diff = Math.round(current - previous);
  if (diff === 0) return <span>same</span>;
  const up = diff > 0;
  const n = Math.abs(diff);
  return (
    <span className={up ? 'td-act-up' : 'td-act-down'}>
      <span aria-hidden="true">{`${up ? '▲' : '▼'} ${n} pts`}</span>
      <span className="td-act-sr">{`${up ? 'Up' : 'Down'} ${n} points`}</span>
    </span>
  );
};

const CountChange = ({ current, previous }) => {
  const text = signedDelta(current, previous);
  if (text == null) return null;
  if (text === 'same') return <span>same</span>;
  const up = current > previous;
  return <span className={up ? 'td-act-up' : 'td-act-down'}>{text}</span>;
};

const dash = (empty, value) => (empty ? '—' : value);

/**
 * The four Exam practice tiles. With no exam work in the period every
 * figure is an em dash; the section still shows its empty copy beside them.
 */
export const ExamStatTiles = ({ summary, empty = false }) => {
  const s = summary || {};
  const weakest = empty ? null : s.weakest;
  return (
    <div className="td-act-glance td-act-examstats">
      <div className="td-stat">
        <span className="td-stat__eyebrow">Accuracy</span>
        <span className="td-stat__value">{dash(empty, s.accuracy == null ? '—' : percent(s.accuracy))}</span>
        <span className="td-stat__caption">
          {empty ? null : <Pts current={s.accuracy} previous={s.accuracy_prev} />}
        </span>
      </div>
      <div className="td-stat">
        <span className="td-stat__eyebrow">Questions answered</span>
        <span className="td-stat__value">{dash(empty, s.answered ?? '—')}</span>
        <span className="td-stat__caption">
          {empty ? null : <CountChange current={s.answered} previous={s.answered_prev} />}
        </span>
      </div>
      <div className="td-stat">
        <span className="td-stat__eyebrow">Papers and mocks</span>
        <span className="td-stat__value">{dash(empty, s.papers ?? '—')}</span>
        <span className="td-stat__caption">
          {empty || s.timed_papers == null ? null : `${s.timed_papers} under exam timing`}
        </span>
      </div>
      <div className="td-stat">
        <span className="td-stat__eyebrow">Weakest topic</span>
        <span className="td-stat__value">{weakest ? weakest.label : '—'}</span>
        <span className="td-stat__caption">
          {weakest ? `${weakest.exam} · ${percent(weakest.accuracy)} of ${weakest.answered}` : null}
        </span>
      </div>
    </div>
  );
};

/** "By exam". A row selects that exam in the section's toggle. */
export const ExamTable = ({ rows, selected, onSelect }) => (
  <div className="td-card td-act-byexam">
    <h4 className="td-card__title">By exam</h4>
    <div className="td-act-scroll">
      <table className="td-act-topics">
        <thead>
          <tr>
            <th scope="col">Exam</th>
            <th scope="col">Accuracy</th>
            <th scope="col">Questions</th>
            <th scope="col">Papers</th>
            <th scope="col">Last practised</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.exam} onClick={() => onSelect(row.exam)} className={row.exam === selected ? 'is-selected' : undefined}>
              <th scope="row">
                <button
                  type="button"
                  aria-label={`Show ${row.label}`}
                  aria-pressed={row.exam === selected}
                  onClick={() => onSelect(row.exam)}
                >
                  {row.label}
                </button>
              </th>
              <td>
                {row.accuracy == null ? 'Writing only' : (
                  <span className="td-act-barnum">
                    <span className="td-act-bar" aria-hidden="true">
                      <i className={row.accuracy < 45 ? 'lo' : (row.accuracy < 70 ? 'mid' : '')} style={{ width: `${Math.min(100, row.accuracy)}%` }} />
                    </span>
                    <span className="td-act-num">{percent(row.accuracy)}</span>
                  </span>
                )}
              </td>
              <td className="td-act-num">{row.answered}</td>
              <td className="td-act-num">{`${row.papers} · ${row.timed} timed`}</td>
              <td>{row.last ? dayMonth(row.last) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

/**
 * Overview's fourth at-a-glance tile. A button: it moves to Exam practice.
 */
export const ExamGlanceTile = ({ examSummary, onJump }) => {
  const summary = examSummary || {};
  const accuracy = summary.accuracy;
  const answered = summary.answered;
  return (
    <button type="button" className="td-stat" onClick={() => onJump('exams')}>
      <span className="td-stat__eyebrow">Exam accuracy</span>
      <span className="td-stat__value">{accuracy == null ? '—' : percent(accuracy)}</span>
      <span className="td-stat__caption">
        <Pts current={accuracy} previous={summary.accuracy_prev} />
        {answered == null ? null : ` · ${answered} questions`}
      </span>
    </button>
  );
};
